import type { Id } from '@hq/utils';
import { toQuery, type HttpClient } from '../client';

/** 会话类型（`sv_message.session_type`）。 */
export type ServiceSessionType = 'WORKORDER' | 'SATISFACTION' | 'CONSULT';

/** 消息发送方（`USER` 采购人 / `STAFF` 客服 / `SYSTEM` 系统）。 */
export type ServiceSenderType = 'USER' | 'STAFF' | 'SYSTEM';

export interface ServiceMessageRow {
  id: Id;
  threadNo: string;
  turnNo: number;
  sessionType: string;
  bizType: string | null;
  bizId: Id;
  bizNo: string | null;
  senderType: string;
  senderUserId: Id;
  senderName: string | null;
  receiverType: string;
  receiverUserId: Id;
  channel: string;
  contentType: string;
  content: string | null;
  attachIds: string | null;
  /** 是否会话首次客服响应（用于 24h 达标考核）。 */
  firstReplyFlag: boolean;
  responseMinutes: number;
  slaHours: number;
  isOvertime: boolean;
  readAt: string | null;
  sendAt: string;
}

export interface ServiceMessageListQuery {
  threadNo?: string;
  bizType?: string;
  bizId?: Id;
  lastId?: Id;
  size?: number;
}

export interface ServiceMessageSendRequest {
  threadNo?: string;
  sessionType?: ServiceSessionType;
  bizType?: string;
  bizId?: Id;
  bizNo?: string;
  senderType?: ServiceSenderType;
  receiverUserId?: Id;
  channel?: 'INNER' | 'WECHAT_MINI' | 'DINGTALK' | 'SMS';
  contentType?: 'TEXT' | 'IMAGE' | 'FILE' | 'VOICE' | 'SYSTEM';
  content?: string;
  attachIds?: string;
}

export interface ServiceMessageSendResult {
  id: Id;
  threadNo: string;
  turnNo: number;
  firstReply: boolean;
  responseMinutes: number;
  isOvertime: boolean;
}

/** 首次响应时效统计（★客服在岗 / 24h 响应的举证口径）。 */
export interface ServiceMessageStats {
  repliedCount: number;
  onTimeCount: number;
  overtimeCount: number;
  avgResponseMinutes: number;
  /** 0~1，达标率。 */
  onTimeRate: number;
}

/** 消息列表结果：游标续拉（`nextLastId` 回填到 query.lastId）。 */
export interface ServiceMessageListResult {
  items: ServiceMessageRow[];
  count: number;
  hasMore: boolean;
  /** 本页最旧一条的 id，作为下一页的 lastId。 */
  nextLastId: Id;
}

/** 服务会话消息：实时售后回复 / 在线答疑 / 评价回复（docs/03 §K3）。 */
export function createMessageApi(http: HttpClient) {
  return {
    list(query: ServiceMessageListQuery = {}): Promise<ServiceMessageListResult> {
      return http.request<ServiceMessageListResult>({
        path: '/wl/message',
        query: toQuery(query)
      });
    },
    /** 发送消息；`threadNo` 缺省时按 `{bizType}-{bizId}` 生成，首条客服回复会回填响应时长。 */
    send(body: ServiceMessageSendRequest): Promise<ServiceMessageSendResult> {
      return http.request<ServiceMessageSendResult>({ path: '/wl/message', method: 'POST', body });
    },
    /** 标记会话中发给我的消息为已读（后端按 body `{ threadNo }` 绑定）。 */
    read(threadNo: string): Promise<{ threadNo: string; readCount: number }> {
      return http.request<{ threadNo: string; readCount: number }>({
        path: '/wl/message/read',
        method: 'POST',
        body: { threadNo }
      });
    },
    /** 首次响应及时率；`beginId`/`endId` 为主键区间（时间位=send_at）。 */
    stats(query: { beginId?: Id; endId?: Id } = {}): Promise<ServiceMessageStats> {
      return http.request<ServiceMessageStats>({ path: '/wl/message/stats', query: toQuery(query) });
    }
  };
}

export type MessageApi = ReturnType<typeof createMessageApi>;
